import React, { useState, useEffect } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import FormModal from '../../common/FormModal';
import ConfirmModal from '../../common/ConfirmModal';
import FormField from '../../common/FormField';
import FormGrid from '../../common/FormGrid';
import { FaSave, FaEdit, FaTrash, FaTimes, FaPlus, FaSearch, FaBalanceScale, FaTag } from 'react-icons/fa';
import './CatalogoUnidades.css';
import { getApiUrl } from '../../../utils/api';

const CatalogoUnidades = () => {
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [unidadSeleccionada, setUnidadSeleccionada] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [unidadAEliminar, setUnidadAEliminar] = useState(null);
  const toast = useToast();

  // Estado del formulario
  const [formulario, setFormulario] = useState({
    nombre: '',
    prefijo: ''
  });

  // Cargar unidades al montar
  useEffect(() => {
    cargarUnidades();
  }, []);

  const cargarUnidades = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getUnidades' })
      });
      const data = await response.json();
      if (data.success) {
        setUnidades(data.data || []);
      } else {
        toast.error(data.message || 'Error al cargar unidades');
      }
    } catch (error) {
      console.error('Error al cargar unidades:', error);
      toast.error('Error al cargar las unidades');
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormulario({
      ...formulario,
      [name]: value
    });
  };

  // Abrir modal para nueva unidad
  const nuevaUnidad = () => {
    setUnidadSeleccionada(null);
    setFormulario({
      nombre: '',
      prefijo: ''
    });
    setShowModal(true);
  };

  // Limpiar formulario y cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setUnidadSeleccionada(null);
    setFormulario({
      nombre: '',
      prefijo: ''
    });
  };

  // Seleccionar unidad (solo para resaltar visualmente)
  const seleccionarUnidad = (unidad) => {
    setUnidadSeleccionada(unidad);
  };

  // Editar unidad en modal
  const editarUnidad = (unidad) => {
    setUnidadSeleccionada(unidad);
    setFormulario({
      nombre: unidad.nombre || '',
      prefijo: unidad.prefijo || ''
    });
    setShowModal(true);
  };

  // Guardar unidad
  const guardarUnidad = async () => {
    if (!formulario.nombre.trim()) {
      toast.error('El nombre de la unidad es requerido');
      return;
    }

    if (!formulario.prefijo.trim()) {
      toast.error('El prefijo de la unidad es requerido');
      return;
    }

    try {
      setGuardando(true);

      const esEdicion = !!unidadSeleccionada;
      const payload = {
        tipo: esEdicion ? 'actualizarUnidad' : 'crearUnidad',
        nombre: formulario.nombre,
        prefijo: formulario.prefijo
      };

      if (esEdicion) {
        payload.idUnidad = unidadSeleccionada.idUnidad;
      }

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        toast.success(esEdicion ? 'Unidad actualizada correctamente' : 'Unidad creada correctamente');
        handleCloseModal();
        cargarUnidades();
      } else {
        toast.error(data.message || 'Error al guardar la unidad');
      }

    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar la unidad');
    } finally {
      setGuardando(false);
    }
  };

  // Confirmar eliminar unidad
  const confirmarEliminarUnidad = (unidad) => {
    setUnidadAEliminar(unidad);
    setShowDeleteModal(true);
  };

  // Eliminar unidad
  const eliminarUnidad = async () => {
    if (!unidadAEliminar) return;

    try {
      setGuardando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'eliminarUnidad',
          idUnidad: unidadAEliminar.idUnidad
        })
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Unidad eliminada correctamente');
        setShowDeleteModal(false);
        setUnidadAEliminar(null);
        setUnidadSeleccionada(null);
        cargarUnidades();
      } else {
        toast.error(data.message || 'Error al eliminar la unidad');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar la unidad');
    } finally {
      setGuardando(false);
    }
  };

  // Cerrar modal de confirmación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setUnidadAEliminar(null);
  };

  // Filtrar unidades basado en la búsqueda
  const unidadesFiltradas = unidades.filter(unidad => {
    if (!busqueda) return true;

    const textoBusqueda = busqueda.toLowerCase();
    return (
      unidad.nombre?.toLowerCase().includes(textoBusqueda) ||
      unidad.prefijo?.toLowerCase().includes(textoBusqueda)
    );
  });

  // Atajos de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        guardarUnidad();
      }
      if (e.key === 'Escape') {
        if (showModal) {
          handleCloseModal();
        }
      }
      if (e.key === 'Delete' && unidadSeleccionada) {
        confirmarEliminarUnidad(unidadSeleccionada);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [unidadSeleccionada, formulario]);

  return (
    <div className="catalogo-proveedores-excel">
      <div className="excel-header">
        <div className="excel-header-controls">
          <div className="excel-search-container">
            <div className="excel-search-group">
              <FaSearch className="search-icon" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar unidades..."
                className="excel-search-input"
                title="Buscar por nombre o prefijo"
              />
              {busqueda && (
                <button
                  onClick={() => setBusqueda('')}
                  className="excel-clear-search"
                  title="Limpiar búsqueda"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
          <div className="excel-acciones">
            <button
              onClick={nuevaUnidad}
              className="btn-excel btn-nuevo"
              title="Nueva unidad"
            >
              <FaPlus /> Nueva Unidad
            </button>
          </div>
        </div>
      </div>

      {/* Tabla estilo Excel */}
      <div className="excel-table-container">
        <table className="excel-table">
          <thead>
            <tr>
              <th className="excel-th">Nombre</th>
              <th className="excel-th">Prefijo</th>
              <th className="excel-th">Descripción Completa</th>
              <th className="excel-th excel-th-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="excel-loading">Cargando unidades...</td>
              </tr>
            ) : unidadesFiltradas.length === 0 ? (
              <tr>
                <td colSpan="4" className="excel-empty">
                  {busqueda ? `No se encontraron unidades que coincidan con "${busqueda}"` : 'No hay unidades registradas'}
                </td>
              </tr>
            ) : (
              unidadesFiltradas.map(unidad => (
                <tr
                  key={unidad.idUnidad}
                  className={`excel-row ${
                    unidadSeleccionada?.idUnidad === unidad.idUnidad ? 'excel-row-selected' : ''
                  }`}
                  onClick={() => seleccionarUnidad(unidad)}
                >
                  <td className="excel-cell excel-cell-nombre">{unidad.nombre}</td>
                  <td className="excel-cell excel-cell-small">
                    <span className="prefijo-badge">{unidad.prefijo}</span>
                  </td>
                  <td className="excel-cell">{unidad.nombre} ({unidad.prefijo})</td>
                  <td className="excel-cell excel-cell-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        editarUnidad(unidad);
                      }}
                      className="excel-btn-action excel-btn-edit"
                      title="Editar"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmarEliminarUnidad(unidad);
                      }}
                      className="excel-btn-action excel-btn-delete"
                      title="Eliminar"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer con información */}
      <div className="excel-footer">
        <div className="excel-info">
          <span>Total de registros: <strong>{unidades.length}</strong></span>
          {busqueda && (
            <span>| Filtrados: <strong>{unidadesFiltradas.length}</strong></span>
          )}
          {unidadSeleccionada && (
            <span>| Registro seleccionado: <strong>{unidadSeleccionada.nombre}</strong></span>
          )}
        </div>
        <div className="excel-shortcuts">
          <span>Nuevo: Crear unidad | Enter: Editar | Del: Eliminar</span>
        </div>
      </div>

      {/* Modal para crear/editar unidad */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={unidadSeleccionada ? 'Editar Unidad' : 'Nueva Unidad'}
        onSave={guardarUnidad}
        saveLabel={unidadSeleccionada ? 'Actualizar' : 'Crear'}
        isSaving={guardando}
        size="small"
        saveIcon={<FaBalanceScale />}
        maxHeight="35vh"
      >
        <FormGrid columns={2} gap="12px">
          <FormField
            label="Nombre"
            name="nombre"
            value={formulario.nombre}
            onChange={handleInputChange}
            placeholder="Nombre de la unidad"
            required
            icon={<FaBalanceScale />}
          />

          <FormField
            label="Prefijo"
            name="prefijo"
            value={formulario.prefijo}
            onChange={handleInputChange}
            placeholder="Kg, Lt, Pz, etc."
            required
            icon={<FaTag />}
          />
        </FormGrid>
      </FormModal>

      {/* Modal de confirmación para eliminar */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={eliminarUnidad}
        title="Eliminar Unidad"
        message={`¿Está seguro de eliminar la unidad "${unidadAEliminar?.nombre}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        type="danger"
        isLoading={guardando}
      />
    </div>
  );
};

export default CatalogoUnidades;